import type { MsgKey } from "./i18n";
import type { I18nText } from "./locales";
import type { RewardItem } from "./reward";

/**
 * 社交 / 个人中心的两张版本内配置表：加入我们渠道 + 时间线事件类型。
 *
 * 两张表都很小（个位数行），所以都是**整表替换**——逐行 CRUD 会让「删掉一个渠道」
 * 「把两个渠道换个顺序」拆成删 + 加两步，中间态在审计日志里看不懂（同每日奖励）。
 *
 * 界面模型直接就是接口形状：字段一一对应、没有单位换算，再套一层 toServerXxx
 * 只是多一处会漂的地方（每日奖励也是直接用同一份类型）。
 */

export interface SocialChannel {
  /** 渠道标识，客户端领奖时传它（tiktok / discord）。整表替换里它当主键，改它 = 换一个渠道 */
  code: string;
  /** 基准语言名。落库是单独一列，但值始终跟着 nameI18n.en 走——见页面里名称列的注释 */
  name: string;
  nameI18n: I18nText;
  icon: string;
  /** 点击跳转地址。空 = 草稿还没填；有值必须是 https，见 channelUrlIssue */
  url: string;
  /** 与通关奖励 / 每日奖励同形状，编辑复用 RewardDialog、发放走同一条链 */
  rewards: RewardItem[];
  sortOrder: number;
  enabled: boolean;
}

export interface SocialEventType {
  type: string;
  icon: string;
  /** 文案模板全语言表，占位符见 EVENT_TEXT_VARS。改文案不发版（客户端按它渲染） */
  textI18n: I18nText;
  visibility: Visibility;
  /** 允许客户端通过 POST /v1/social/events 上报；0 = 只能服务端产生 */
  clientReportable: boolean;
  sortOrder: number;
  enabled: boolean;
}

/** self = 落在承受者自己的时间线（当前唯一用到的）；friends = 扇出到好友（预留未做） */
export const VISIBILITIES = ["self", "friends"] as const;
export type Visibility = (typeof VISIBILITIES)[number];
export const VISIBILITY_LABEL: Record<Visibility, MsgKey> = {
  self: "se.vis.self",
  friends: "se.vis.friends",
};

/**
 * 服务端结算产生的三种事件（main_attack_log 的三种 kind）。运营能改它们的文案图标，
 * 但**不能开客户端上报**——开了等于让客户端往自己时间线里塞「某某偷了我 999999 金币」。
 *
 * 与服务端 putSocialEventTypes 的 SERVER_ONLY_TYPES 是同一份名单。这里只是把那条
 * 拒绝提前到界面上（勾选框直接禁用并写明原因），**真正的拦截仍在服务端**。
 */
export const SERVER_ONLY_TYPES = ["steal", "thunder_hit", "thunder_block"];
export const isServerOnly = (type: string) => SERVER_ONLY_TYPES.includes(type);

/**
 * 文案模板里可用的占位符。服务端只给 type + params，渲染在客户端。
 * 写错的占位符**不会报错**，只会在玩家那边原样显示成一串 `{amout}`，所以列在界面上。
 */
export const EVENT_TEXT_VARS = ["{actor}", "{amount}", "{building_idx}"];

/** 与服务端 putSocialChannels / putSocialEventTypes 的上限同一个数 */
export const CHANNEL_MAX = 50;
export const EVENT_TYPE_MAX = 100;

/** 与服务端的 CODE_RE / EVENT_TYPE_RE 同一条正则：写入侧就是按它拒的 */
const CODE_RE = /^[a-z][a-z0-9_-]{0,63}$/;
const TYPE_RE = /^[a-z][a-z0-9_]{0,31}$/;

// 展示顺序与服务端读出的 ORDER BY sort_order, code 一致——两边不一致的话，
// 保存后表格会自己跳一次行序，看着像配置被改了
export const sortChannels = (xs: SocialChannel[]): SocialChannel[] =>
  [...xs].sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
export const sortEventTypes = (xs: SocialEventType[]): SocialEventType[] =>
  [...xs].sort((a, b) => a.sortOrder - b.sortOrder || a.type.localeCompare(b.type));

/** 新增一行：只把排序号接到最后，其余留空待填——替运营猜 code 和奖励等于塞假配置 */
export const newChannel = (xs: SocialChannel[]): SocialChannel => ({
  code: "", name: "", nameI18n: {}, icon: "", url: "", rewards: [],
  sortOrder: xs.reduce((m, c) => Math.max(m, c.sortOrder), 0) + 1,
  enabled: true,
});
export const newEventType = (xs: SocialEventType[]): SocialEventType => ({
  type: "", icon: "", textI18n: {}, visibility: "self", clientReportable: false,
  sortOrder: xs.reduce((m, e) => Math.max(m, e.sortOrder), 0) + 1,
  enabled: true,
});

/** code 格上的问题。空 code 也算不合法：整表替换拿它当主键，没有它这行存不下去 */
export type CodeIssue = "sc.issue.code" | "sc.issue.dup";
export function channelCodeIssue(c: SocialChannel, all: SocialChannel[]): CodeIssue | null {
  if (!CODE_RE.test(c.code)) return "sc.issue.code";
  if (all.filter((x) => x.code === c.code).length > 1) return "sc.issue.dup";
  return null;
}

/**
 * 跳转地址：空串放行（草稿还没填），有值必须是**完整的 https 地址**——
 * http 会被 App 的 ATS 直接拦掉，玩家点了什么也不会发生。
 */
export function channelUrlIssue(url: string): "sc.issue.url" | null {
  const s = url.trim();
  if (!s) return null;
  try { return new URL(s).protocol === "https:" ? null : "sc.issue.url"; }
  catch { return "sc.issue.url"; }
}

export type TypeIssue = "se.issue.type" | "se.issue.dup";
export function eventTypeIssue(e: SocialEventType, all: SocialEventType[]): TypeIssue | null {
  if (!TYPE_RE.test(e.type)) return "se.issue.type";
  if (all.filter((x) => x.type === e.type).length > 1) return "se.issue.dup";
  return null;
}
